<template>
    <div class="row">
        <div class="col-sm-12">
            <form @submit.prevent="validateBeforeSubmit">
                <template v-if="isEdit">
                    <div class="b-form-group form-group">
                        <div class="form-row">
                            <label class="col-sm-2 col-form-label" for="name">{{$t('permissions.forms.name')}}</label>
                            <div class="col-sm-5">
                                <input class="form-control"
                                       data-vv-as="nombre"
                                       id="name"
                                       name="name"
                                       type="text"
                                       v-model="form.name"
                                       v-validate="'required'">
                                <div class="invalid-feedback" v-show="errors.has('name')">
                                    {{ errors.first('name') }}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="b-form-group form-group">
                        <div class="form-row">
                            <label class="col-sm-2 col-form-label" for="slug">
                                {{$t('permissions.forms.slug')}}
                                <font-awesome-icon :icon="['fas', 'lock']"
                                                    class="text-muted ml-1"
                                                    title="El slug no se puede editar: el código lo usa para validar permisos"/>
                            </label>
                            <div class="col-sm-5">
                                <input class="form-control"
                                       data-vv-as="slug"
                                       id="slug"
                                       name="slug"
                                       readonly
                                       type="text"
                                       v-model="form.slug">
                            </div>
                        </div>
                    </div>

                    <div class="b-form-group form-group">
                        <div class="form-row">
                            <label class="col-sm-2 col-form-label" for="permission_module_id">Módulo</label>
                            <div class="col-sm-5">
                                <select class="form-control"
                                        id="permission_module_id"
                                        v-model="form.permission_module_id">
                                    <option :value="null">Seleccionar módulo</option>
                                    <option :key="module.value" :value="module.value" v-for="module in modules">
                                        {{ module.text }}
                                    </option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <div class="b-form-group form-group">
                        <div class="form-row">
                            <label class="col-sm-2 col-form-label"
                                   for="description">{{$t('permissions.forms.description')}}</label>
                            <div class="col-sm-5">
                                <textarea class="form-control"
                                          id="description"
                                          rows="2"
                                          v-model="form.description"></textarea>
                            </div>
                        </div>
                    </div>
                </template>

                <template v-else>
                    <div class="alert alert-info">
                        Agrega uno o varios permisos: cada tarjeta se crea como un registro independiente al
                        enviar. Usa <strong>Recurso + Acción</strong> para generar el slug solo, o
                        "Escribir slug manualmente" para uno personalizado.
                    </div>

                    <div class="card mb-3" v-for="(row, index) in rows" :key="row.id">
                        <div class="card-body">
                            <div class="d-flex justify-content-between align-items-center mb-2">
                                <h6 class="mb-0">Permiso {{ index + 1 }}</h6>
                                <button class="btn btn-sm btn-outline-danger"
                                        type="button"
                                        v-if="rows.length > 1"
                                        @click="removeRow(index)">
                                    <font-awesome-icon :icon="['fas', 'trash']"/>
                                </button>
                            </div>

                            <div class="form-row" v-if="row.mode === 'builder'">
                                <div class="col-sm-4">
                                    <label :for="'resource-' + row.id">Recurso</label>
                                    <input class="form-control"
                                           :id="'resource-' + row.id"
                                           placeholder="ej. chains, hotels"
                                           type="text"
                                           v-model="row.resource"
                                           @input="updateRowSlug(row)">
                                </div>
                                <div class="col-sm-4">
                                    <label :for="'action-' + row.id">Acción</label>
                                    <select class="form-control"
                                            :id="'action-' + row.id"
                                            v-model="row.action"
                                            @change="updateRowSlug(row)">
                                        <option :disabled="isActionUsed(row, choice.key)"
                                                :key="choice.key"
                                                :value="choice.key"
                                                v-for="choice in actionChoices">
                                            {{ choice.label }}{{ isActionUsed(row, choice.key) ? ' (ya usada)' : '' }}
                                        </option>
                                        <option value="custom">Personalizada...</option>
                                    </select>
                                </div>
                                <div class="col-sm-4" v-if="row.action === 'custom'">
                                    <label :for="'customAction-' + row.id">Acción personalizada</label>
                                    <input class="form-control"
                                           :id="'customAction-' + row.id"
                                           placeholder="ej. export, approve"
                                           type="text"
                                           v-model="row.customAction"
                                           @input="updateRowSlug(row)">
                                </div>
                            </div>

                            <div class="form-row mt-2">
                                <div class="col-sm-12">
                                    <label :for="'module-' + row.id">Módulo</label>
                                    <select class="form-control"
                                            :id="'module-' + row.id"
                                            :name="'module-' + row.id"
                                            data-vv-as="módulo"
                                            v-model="row.permission_module_id"
                                            v-validate="'required'">
                                        <option :value="null">Seleccionar módulo</option>
                                        <option :key="module.value" :value="module.value" v-for="module in modules">
                                            {{ module.text }}
                                        </option>
                                    </select>
                                    <div class="invalid-feedback d-block" v-show="errors.has('module-' + row.id)">
                                        {{ errors.first('module-' + row.id) }}
                                    </div>
                                </div>
                            </div>

                            <div class="form-row mt-2">
                                <div class="col-sm-4">
                                    <label :for="'name-' + row.id">{{$t('permissions.forms.name')}}</label>
                                    <input class="form-control"
                                           :id="'name-' + row.id"
                                           :name="'name-' + row.id"
                                           data-vv-as="nombre"
                                           type="text"
                                           v-model="row.name"
                                           v-validate="'required'"
                                           @input="row.nameTouched = true">
                                    <div class="invalid-feedback d-block" v-show="errors.has('name-' + row.id)">
                                        {{ errors.first('name-' + row.id) }}
                                    </div>
                                </div>

                                <div class="col-sm-4">
                                    <label :for="'slug-' + row.id">Slug</label>
                                    <input class="form-control"
                                           :class="{ 'is-invalid': !!rowSlugStatus(row) }"
                                           :id="'slug-' + row.id"
                                           :name="'slug-' + row.id"
                                           :readonly="row.mode === 'builder'"
                                           data-vv-as="slug"
                                           type="text"
                                           v-model="row.slug"
                                           v-validate="'required|regex:^[a-z0-9]+(\\.[a-z0-9]+)*$'">
                                    <div class="invalid-feedback d-block" v-show="errors.has('slug-' + row.id)">
                                        {{ errors.first('slug-' + row.id) }}
                                    </div>
                                    <div class="text-danger small mt-1" v-if="rowSlugStatus(row) === 'exists'">
                                        Ya existe un permiso con este slug.
                                    </div>
                                    <div class="text-danger small mt-1" v-if="rowSlugStatus(row) === 'duplicate'">
                                        Repetiste este slug en otra tarjeta.
                                    </div>
                                    <button class="btn btn-link btn-sm p-0 mt-1" type="button" @click="toggleRowMode(row)">
                                        {{ row.mode === 'builder' ? 'Escribir slug manualmente' : 'Generar con recurso y acción' }}
                                    </button>
                                </div>

                                <div class="col-sm-4">
                                    <label :for="'description-' + row.id">{{$t('permissions.forms.description')}}</label>
                                    <textarea class="form-control"
                                              :id="'description-' + row.id"
                                              rows="1"
                                              v-model="row.description"
                                              @input="row.descriptionTouched = true"></textarea>
                                </div>
                            </div>
                        </div>
                    </div>

                    <button class="btn btn-outline-primary btn-sm mb-3" type="button" @click="addRow">
                        <font-awesome-icon :icon="['fas', 'plus']"/> Agregar otro permiso
                    </button>
                </template>
            </form>
        </div>
        <div class="col-sm-6">
            <div slot="footer">
                <img src="/images/loading.svg" v-if="loading" width="40px"/>
                <button @click="validateBeforeSubmit"
                        class="btn btn-success"
                        type="submit"
                        :disabled="submitDisabled"
                        v-if="!loading && $can(isEdit ? 'update' : 'create', 'permissions')">
                    <font-awesome-icon :icon="['fas', 'dot-circle']"/>
                    {{$t('global.buttons.submit')}}
                </button>
                <router-link :to="{ name: 'PermissionsList' }" v-if="!loading">
                    <button class="btn btn-danger" type="reset">
                        {{$t('global.buttons.cancel')}}
                    </button>
                </router-link>
            </div>
        </div>
    </div>
</template>

<script>
  import { API } from './../../api'

  const ACTION_CHOICES = [
    { key: 'create', label: 'Crear' },
    { key: 'read', label: 'Leer' },
    { key: 'update', label: 'Actualizar' },
    { key: 'delete', label: 'Eliminar' }
  ]

  const ACTION_LABELS = ACTION_CHOICES.reduce((labels, choice) => {
    labels[choice.key] = choice.label
    return labels
  }, {})

  export default {
    data: () => {
      return {
        loading: false,
        formAction: 'post',
        existingSlugs: [],
        modules: [],
        originalSlug: '',
        rowSeq: 1,
        rows: [],
        form: {
          name: '',
          slug: '',
          description: '',
          permission_module_id: null
        }
      }
    },
    computed: {
      actionChoices () {
        return ACTION_CHOICES
      },
      isEdit () {
        return this.$route.params.id !== undefined
      },
      slugExists () {
        if (!this.form.slug) {
          return false
        }

        const slug = this.form.slug.toLowerCase().trim()
        return slug !== this.originalSlug && this.existingSlugs.indexOf(slug) !== -1
      },
      submitDisabled () {
        if (this.isEdit) {
          return this.slugExists
        }

        return this.rows.some((row) => {
          return !row.name.trim() || !row.slug.trim() || !row.permission_module_id || !!this.rowSlugStatus(row)
        })
      }
    },
    mounted () {
      this.rows = [this.createRow()]

      API.get('/permissions/modules').then((result) => {
        if (result.data.success === true) {
          this.modules = result.data.data
        }
      })

      API.get('/permissions?limit=100000').then((result) => {
        if (result.data.success === true) {
          this.existingSlugs = result.data.data.map((permission) => (permission.slug || '').toLowerCase().trim())
        }
      })

      if (this.isEdit) {
        API.get('/permissions/' + this.$route.params.id)
          .then((result) => {
            this.form = result.data.data
            this.formAction = 'put'
            this.originalSlug = (this.form.slug || '').toLowerCase().trim()
          })
      }
    },
    methods: {
      slugify (value) {
        return (value || '')
          .toString()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase()
          .trim()
          .replace(/[^a-z0-9]+/g, '')
      },
      createRow () {
        const row = {
          id: this.rowSeq++,
          mode: 'builder',
          resource: '',
          action: 'create',
          customAction: '',
          slug: '',
          name: '',
          permission_module_id: null,
          nameTouched: false,
          description: '',
          descriptionTouched: false
        }

        this.updateRowSlug(row)

        return row
      },
      addRow () {
        this.rows.push(this.createRow())
      },
      removeRow (index) {
        this.rows.splice(index, 1)
      },
      toggleRowMode (row) {
        row.mode = row.mode === 'builder' ? 'custom' : 'builder'

        if (row.mode === 'builder') {
          this.updateRowSlug(row)
        }
      },
      updateRowSlug (row) {
        if (row.mode !== 'builder') {
          return
        }

        if (row.action !== 'custom') {
          const used = this.usedActionsForResource(row)
          if (used.indexOf(row.action) !== -1) {
            const freeAction = ACTION_CHOICES.find((choice) => used.indexOf(choice.key) === -1)
            if (freeAction) {
              row.action = freeAction.key
            }
          }
        }

        const resource = this.slugify(row.resource)
        const action = row.action === 'custom' ? this.slugify(row.customAction) : row.action

        row.slug = action ? (resource ? `${resource}.${action}` : action) : resource

        if (!row.nameTouched) {
          row.name = this.suggestRowName(row)
        }

        if (!row.descriptionTouched) {
          row.description = this.suggestRowDescription(row)
        }
      },
      usedActionsForResource (row) {
        const resource = this.slugify(row.resource)

        if (!resource) {
          return []
        }

        const prefix = resource + '.'

        const fromExisting = this.existingSlugs
          .filter((slug) => slug.indexOf(prefix) === 0)
          .map((slug) => slug.substring(prefix.length))

        const fromRows = this.rows
          .filter((r) => r.id !== row.id && r.mode === 'builder' && r.action !== 'custom' && this.slugify(r.resource) === resource)
          .map((r) => r.action)

        return fromExisting.concat(fromRows)
      },
      isActionUsed (row, action) {
        return this.usedActionsForResource(row).indexOf(action) !== -1
      },
      suggestRowName (row) {
        const resourceLabel = row.resource.trim()
        const actionLabel = row.action === 'custom' ? row.customAction.trim() : (ACTION_LABELS[row.action] || '')

        return [actionLabel, resourceLabel].filter(Boolean).join(' ')
      },
      suggestRowDescription (row) {
        const resourceLabel = row.resource.trim()
        const actionLabel = row.action === 'custom' ? row.customAction.trim() : (ACTION_LABELS[row.action] || '')

        if (!resourceLabel || !actionLabel) {
          return ''
        }

        return `Permite ${actionLabel.toLowerCase()} ${resourceLabel}`
      },
      rowSlugStatus (row) {
        const slug = (row.slug || '').toLowerCase().trim()

        if (!slug) {
          return null
        }

        if (this.existingSlugs.indexOf(slug) !== -1) {
          return 'exists'
        }

        const timesUsed = this.rows.filter((r) => (r.slug || '').toLowerCase().trim() === slug).length
        if (timesUsed > 1) {
          return 'duplicate'
        }

        return null
      },
      validateBeforeSubmit () {
        if (this.isEdit) {
          this.$validator.validateAll().then((result) => {
            if (!result) {
              this.$notify({
                group: 'main',
                type: 'error',
                title: 'Permisos',
                text: 'Completa los campos requeridos.'
              })
              return
            }

            if (this.slugExists) {
              this.$notify({
                group: 'main',
                type: 'error',
                title: 'Permisos',
                text: 'Ya existe un permiso con este slug.'
              })
              return
            }

            this.submit()
          })
          return
        }

        this.$validator.validateAll().then((result) => {
          if (!result) {
            this.$notify({
              group: 'main',
              type: 'error',
              title: 'Permisos',
              text: 'Completa los campos requeridos en cada permiso.'
            })
            return
          }

          if (this.submitDisabled) {
            this.$notify({
              group: 'main',
              type: 'error',
              title: 'Permisos',
              text: 'Revisa los slugs duplicados o repetidos antes de continuar.'
            })
            return
          }

          this.submitBulk()
        })
      },
      submit () {
        this.loading = true

        API({
          method: this.formAction,
          url: 'permissions/' + (this.isEdit ? this.$route.params.id : ''),
          data: this.form
        })
          .then((result) => {
            if (result.data.success === true) {
              this.$router.push({ name: 'PermissionsList' })
            } else {
              this.$notify({
                group: 'main',
                type: 'error',
                title: 'Permisos',
                text: result.data.message
              })

              this.loading = false
            }
          })
          .catch(() => {
            this.$notify({
              group: 'main',
              type: 'error',
              title: 'Permisos',
              text: 'Ocurrió un error al guardar el permiso. Verifica que el slug no esté duplicado.'
            })

            this.loading = false
          })
      },
      submitBulk () {
        this.loading = true

        Promise.all(this.rows.map((row) => API({
          method: 'post',
          url: 'permissions',
          data: {
            name: row.name,
            slug: row.slug,
            description: row.description,
            permission_module_id: row.permission_module_id
          }
        })
          .then(() => ({ ok: true, row }))
          .catch(() => ({ ok: false, row }))
        )).then((results) => {
          const failed = results.filter((result) => !result.ok)

          if (failed.length > 0) {
            this.$notify({
              group: 'main',
              type: 'error',
              title: 'Permisos',
              text: 'No se pudieron crear: ' + failed.map((result) => result.row.slug).join(', ')
            })
          }

          if (failed.length < results.length) {
            this.$router.push({ name: 'PermissionsList' })
          } else {
            this.loading = false
          }
        })
      }
    }
  }
</script>

<style lang="stylus">

</style>
